import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from './../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSearchMutaion from './../../../../hooks/apis/useSearchMutaion';
import Commons from '../../../../Services/Common/Common';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { SCH_BASIC_Schema } from './SCH_BASIC_Schema';

/**
 * 일정(Schedule) 관련 React Query 훅 모음
 * - 기본 조회(useQuery): useCalendarList, useCalendarEvents
 * - 조건 조회(useMutation 기반): useSharedCalendar, useSupplierCalendar, useViewingMeCalendar
 * - 삭제/저장(useMutation 기반): useDeleteCalendar, useSaveCalendar, useSaveEvent 등
 *
 * 만약 MUTATION 훅에서 공통 응답 처리 외에 추가 성공 콜백이 필요하면
 * successOptions 파라미터로 onSuccess 콜백 함수를 넘겨줄 수 있다.
 */

/**
 * 내 캘린더 목록 조회
 * API: getCalendarR1
 * 용도: 사용자가 생성한 내 캘린더 목록을 조회합니다.
 * 반환: 캘린더 ID, 설명, 색상, 공개여부, 소유자 ID, 메인 캘린더 여부 등
 */
export const useSearch1 = (options = {}) => {
  const { onSuccess } = options;
  const params = mapParamsBySchema(SCH_BASIC_Schema.mapper.search1);

  const query = useQuery({
    queryKey: ['calendar', 'getCalendarR1', params],
    queryFn: () =>
      apiRequest('post', Global.urls.Calendar.getCalendarR1, params),
    enabled: true, // 자동 실행 여부
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/**
 * 일정 조회 (날짜 범위 기반)
 * API: getCalendarR2
 * 용도: 지정된 날짜 범위 내의 일정을 조회합니다. 반복 일정 인스턴스도 포함됩니다.
 * 파라미터: UserId, StartDt (암호화), EndDt (암호화)
 * 반환: 일정 ID, 제목, 설명, 위치, 시작일시, 종료일시, 반복 설정, 알람 정보 등
 */
export const useSearch2 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SCH_BASIC_Schema.mapper.search2,
      context
    );

    return apiRequest(
      'post',
      Global.urls.Calendar.getCalendarR2,
      encryptedParams
    );
  }, successOptions);
};

/**
 * 공유 캘린더 조회 / 내가 등록한 관심 캘린더 조회
 * API: getCalendarR3
 * 용도: 공유 캘린더 목록을 조회하거나, 내가 등록한 관심 캘린더 목록을 조회합니다.
 * 반환: 공유 캘린더 ID, 이름, 캘린더명, 색상, 공개여부, 신청대기 여부 등
 */
export const useSearch3 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SCH_BASIC_Schema.mapper.search3,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Calendar.getCalendarR3,
      encryptedParams
    );
  }, successOptions);
};

/**
 * 공유자 검색
 * API: getCalendarR4
 * 용도: 공유 캘린더에 추가할 공유자(사용자)를 검색합니다.
 * 파라미터: 검색 키워드 (이름, 아이디 등)
 * 반환: 검색된 사용자 목록 (사용자 ID, 이름 등)
 */
export const useSearch4 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SCH_BASIC_Schema.mapper.search4,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Calendar.getCalendarR4,
      encryptedParams
    );
  }, successOptions);
};

/**
 * 공유자의 캘린더 조회
 * API: getCalendarR5
 * 용도: 특정 사용자(공유자)가 소유한 캘린더 목록을 조회합니다.
 * 파라미터: userId (공유자의 사용자 ID)
 * 반환: 공유자의 캘린더 목록 (캘린더 ID, 설명, 색상, 공개여부 등)
 */
export const useSearch5 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SCH_BASIC_Schema.mapper.search5,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Calendar.getCalendarR5,
      encryptedParams
    );
  }, successOptions);
};

/**
 * 내 일정을 보고 있는 동료 조회
 * API: getCalendarR6
 * 용도: 내 일정을 보고 있는 동료(공유 요청을 보낸 사용자) 목록을 조회합니다.
 * 반환: 동료 목록 (사용자 ID, 이름, 캘린더 정보, 신청대기 여부 등)
 */
export const useSearch6 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      SCH_BASIC_Schema.mapper.search6,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Calendar.getCalendarR6,
      encryptedParams
    );
  }, successOptions);
};

/**
 * 일정 저장/수정
 * API: saveCalBasicU1
 * 용도: 신규 일정을 등록하거나 기존 일정을 수정합니다.
 * 파라미터: 일정 ID, 제목, 설명, 위치, 시작일시, 종료일시, 종일여부, 반복 설정, 알람 설정 등
 * 반환: 저장 결과 (성공/실패, 메시지)
 */
export const useSave1 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(
      rows,
      SCH_BASIC_Schema.mapper.save1,
      context
    );
    return apiRequest('post1', Global.urls.Calendar.saveCalBasicU1, payload);
  }, options);
};

/**
 * 캘린더 공개/비공개 설정 변경
 * API: saveCalBasicU2
 * 용도: 내 캘린더 또는 공유 캘린더의 공개/비공개 설정을 변경합니다.
 * 파라미터: 캘린더 ID, 구분(gubun), 사용자 ID, 공개여부(isPublic), 공유 캘린더 ID(calMId)
 * 반환: 저장 결과 (성공/실패, 메시지)
 */
export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(
      rows,
      SCH_BASIC_Schema.mapper.save2,
      context
    );
    return apiRequest('post1', Global.urls.Calendar.saveCalBasicU2, payload);
  }, options);
};

/**
 * 캘린더 저장/수정 (이름, 색상 등)
 * API: saveCalBasicU3
 * 용도: 내 캘린더를 신규 생성하거나 기존 캘린더의 이름, 색상 등을 수정합니다.
 * 파라미터: 캘린더 ID(신규는 0), 설명(이름), 색상, 공개여부
 * 반환: 저장 결과 (성공/실패, 메시지)
 */
export const useSave3 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(
      rows,
      SCH_BASIC_Schema.mapper.save3,
      context
    );
    return apiRequest('post1', Global.urls.Calendar.saveCalBasicU3, payload);
  }, options);
};

/**
 * 공유 캘린더 색상 변경 / 공유 캘린더 추가
 * API: saveCalBasicU4
 * 용도: 공유 캘린더의 색상을 변경하거나 새로운 공유 캘린더를 추가합니다.
 * 파라미터: 공유 캘린더 ID(calMId), 캘린더 ID(calId), 사용자 ID, 색상
 * 반환: 저장 결과 (성공/실패, 메시지)
 */
export const useSave4 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(
      rows,
      SCH_BASIC_Schema.mapper.save4,
      context
    );
    return apiRequest('post1', Global.urls.Calendar.saveCalBasicU4, payload);
  }, options);
};

/**
 * 공유 캘린더 신청 수락
 * API: saveCalBasicU5
 * 용도: 내 일정을 보고 있는 동료의 공유 요청을 수락합니다.
 * 파라미터: 공유 캘린더 ID(calMId)
 * 반환: 저장 결과 (성공/실패, 메시지)
 */
export const useSave5 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(
      rows,
      SCH_BASIC_Schema.mapper.save5,
      context
    );
    return apiRequest('post1', Global.urls.Calendar.saveCalBasicU5, payload);
  }, options);
};

/**
 * 내 캘린더 삭제
 * API: deleteCalBasicD1
 * 용도: 사용자가 생성한 내 캘린더를 삭제합니다. (메인 캘린더는 삭제 불가)
 * 파라미터: 캘린더 ID(calId)
 * 반환: 삭제 결과 (성공/실패, 메시지)
 */
export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SCH_BASIC_Schema.mapper.delete1);
    return apiRequest('post1', Global.urls.Calendar.deleteCalBasicD1, payload);
  }, options);
};

/**
 * 공유 캘린더 삭제
 * API: deleteCalBasicD2
 * 용도: 공유 캘린더를 삭제합니다. (내가 등록한 관심 캘린더 또는 내 일정을 보고 있는 동료 삭제)
 * 파라미터: 공유 캘린더 ID(calMId)
 * 반환: 삭제 결과 (성공/실패, 메시지)
 */
export const useDelete2 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SCH_BASIC_Schema.mapper.delete2);
    return apiRequest('post1', Global.urls.Calendar.deleteCalBasicD2, payload);
  }, options);
};

/**
 * 일정 삭제
 * API: deleteCalBasicD3
 * 용도: 등록된 일정을 삭제합니다. 반복 일정의 경우 모든 인스턴스가 삭제됩니다.
 * 파라미터: 일정 ID(calEId), 사용자 정보
 * 반환: 삭제 결과 (성공/실패, 메시지)
 */
export const useDelete3 = (options = {}) => {
  return useDeleteMutation(async (rows, context) => {
    const payload = mapRowsBySchema(
      rows,
      SCH_BASIC_Schema.mapper.delete3,
      context
    );
    return apiRequest('post1', Global.urls.Calendar.deleteCalBasicD3, payload);
  }, options);
};
